// DESCRIPTION: BookingFilters — search + status + date-range toolbar for the bookings admin list.
// DESCRIPTION: Live debounced search is handled by the parent (BookingList) via useDebounce. Design-system tokens only.

import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Filter/search toolbar for the bookings list.
 *
 * Search is controlled by `searchValue`/`onSearchChange` and fires on every
 * keystroke; the parent debounces it. Status and date changes fire immediately
 * and are merged into the parent's `filters` object.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.searchValue      Live search input value.
 * @param {Function} props.onSearchChange   Called on every keystroke with the new value.
 * @param {Object}   props.filters          Current filter values ({ status, event_id, date_from, date_to }).
 * @param {Function} props.onFilterChange   Called with a partial object to merge into filters.
 * @param {Function} props.onClear          Called when the user clears all filters.
 * @param {boolean}  props.hasActiveFilters Whether any filter is active (shows the clear button).
 *
 * @return {JSX.Element} The BookingFilters component.
 */
const BookingFilters = ( {
	searchValue,
	onSearchChange,
	filters,
	onFilterChange,
	onClear,
	hasActiveFilters,
} ) => {
	const [ isExpanded, setIsExpanded ] = useState( false );

	const statusOptions = [
		{ label: __( 'All statuses', 'nemtly-booking' ), value: '' },
		{ label: __( 'Pending', 'nemtly-booking' ), value: 'pending' },
		{ label: __( 'Confirmed', 'nemtly-booking' ), value: 'confirmed' },
		{ label: __( 'Cancelled', 'nemtly-booking' ), value: 'cancelled' },
		{ label: __( 'Waitlisted', 'nemtly-booking' ), value: 'waitlisted' },
	];

	const controlClass =
		'nb-h-9 nb-px-3 nb-rounded-md nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-text-sm nb-text-neutral-700 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-accent-500 focus:nb-border-accent-500 nb-transition-colors nb-duration-fast nb-cursor-pointer';

	// Search input uses explicit left padding (pl-9) for the leading icon instead
	// of px-3, so the placeholder text never overlaps the icon.
	// Utilities are marked important (!) because WordPress admin's unlayered
	// input[type="text"] rule would otherwise override Tailwind's layered padding/height.
	const inputClass =
		'!nb-h-9 !nb-pl-9 !nb-pr-3 !nb-rounded-md !nb-border !nb-border-neutral-300 !nb-bg-neutral-0 !nb-text-sm !nb-text-neutral-700 focus:!nb-outline-none focus:!nb-ring-2 focus:!nb-ring-accent-500 focus:!nb-border-accent-500 !nb-transition-colors !nb-duration-fast !nb-w-full';

	// Count active non-search filters for the "More" badge.
	const activeFilterCount = [
		filters.status,
		filters.date_from,
		filters.date_to,
	].filter( ( v ) => v ).length;
	const moreActiveCount =
		( filters.date_from ? 1 : 0 ) + ( filters.date_to ? 1 : 0 );

	return (
		<div className="nb-bg-neutral-0 nb-border nb-border-neutral-200 nb-shadow-elevation-1 nb-mb-4 nb-overflow-hidden">
			{ /* Compact filter bar — always visible */ }
			<div className="nb-px-5 nb-py-3">
				<div className="nb-flex nb-items-center nb-gap-3 nb-flex-wrap">
					{ /* Search input */ }
					<div className="nb-relative nb-flex-1 nb-min-w-[200px] nb-max-w-sm">
						<div className="nb-absolute nb-inset-y-0 nb-left-0 nb-flex nb-items-center nb-pl-3 nb-pointer-events-none">
							<svg
								className="nb-w-4 nb-h-4 nb-text-neutral-400"
								fill="none"
								viewBox="0 0 16 16"
								aria-hidden="true"
							>
								<circle
									cx="7"
									cy="7"
									r="4.5"
									stroke="currentColor"
									strokeWidth="1.4"
								/>
								<path
									d="M10.5 10.5L14 14"
									stroke="currentColor"
									strokeWidth="1.4"
									strokeLinecap="round"
								/>
							</svg>
						</div>
						<input
							type="text"
							value={ searchValue }
							onChange={ ( e ) =>
								onSearchChange( e.target.value )
							}
							placeholder={ __(
								'Search bookings…',
								'nemtly-booking'
							) }
							className={ inputClass }
							aria-label={ __(
								'Search bookings',
								'nemtly-booking'
							) }
						/>
					</div>

					{ /* Status dropdown */ }
					<select
						value={ filters.status }
						onChange={ ( e ) =>
							onFilterChange( { status: e.target.value } )
						}
						className={ controlClass }
						aria-label={ __(
							'Filter by status',
							'nemtly-booking'
						) }
					>
						{ statusOptions.map( ( option ) => (
							<option key={ option.value } value={ option.value }>
								{ option.label }
							</option>
						) ) }
					</select>

					{ /* More filters toggle (date range) */ }
					<button
						type="button"
						onClick={ () => setIsExpanded( ! isExpanded ) }
						className="nb-inline-flex nb-items-center nb-gap-1 nb-px-3 nb-h-9 nb-text-sm nb-text-neutral-600 hover:nb-text-neutral-900 nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-rounded-md hover:nb-bg-neutral-50 nb-transition-colors nb-duration-fast nb-cursor-pointer"
					>
						<svg
							className="nb-w-4 nb-h-4"
							fill="none"
							viewBox="0 0 16 16"
							aria-hidden="true"
						>
							<path
								d="M2 4h12M4 8h8M6 12h4"
								stroke="currentColor"
								strokeWidth="1.4"
								strokeLinecap="round"
							/>
						</svg>
						{ isExpanded
							? __( 'Less', 'nemtly-booking' )
							: __( 'More', 'nemtly-booking' ) }
						{ ! isExpanded && moreActiveCount > 0 && (
							<span className="nb-inline-flex nb-items-center nb-justify-center nb-min-w-5 nb-h-5 nb-px-1 nb-text-xs nb-font-medium nb-bg-accent-500 nb-text-neutral-0 nb-rounded-full">
								{ moreActiveCount }
							</span>
						) }
					</button>

					{ /* Clear */ }
					{ hasActiveFilters && (
						<button
							type="button"
							onClick={ onClear }
							className="nb-text-sm nb-text-neutral-500 hover:nb-text-neutral-900 nb-underline nb-cursor-pointer nb-transition-colors nb-duration-fast"
						>
							{ __( 'Clear', 'nemtly-booking' ) }
						</button>
					) }
				</div>
			</div>

			{ /* Expanded date filters — toggled */ }
			{ isExpanded && (
				<div className="nb-px-5 nb-py-3 nb-border-t nb-border-neutral-200 nb-bg-neutral-50">
					<div className="nb-flex nb-items-center nb-gap-4 nb-flex-wrap">
						<label className="nb-flex nb-items-center nb-gap-2 nb-text-sm nb-text-neutral-600">
							{ __( 'From', 'nemtly-booking' ) }
							<input
								type="date"
								value={ filters.date_from }
								onChange={ ( e ) =>
									onFilterChange( {
										date_from: e.target.value,
									} )
								}
								className={ `${ controlClass } nb-h-9` }
							/>
						</label>
						<label className="nb-flex nb-items-center nb-gap-2 nb-text-sm nb-text-neutral-600">
							{ __( 'To', 'nemtly-booking' ) }
							<input
								type="date"
								value={ filters.date_to }
								onChange={ ( e ) =>
									onFilterChange( {
										date_to: e.target.value,
									} )
								}
								className={ `${ controlClass } nb-h-9` }
							/>
						</label>
					</div>
				</div>
			) }
		</div>
	);
};

export default BookingFilters;
